/**
 * Экранная клавиатура для любого поля.
 *
 * На Windows-планшете системная клавиатура не всплывает при касании:
 * в режиме ноутбука её нет вовсе, в планшетном она зависит от настройки,
 * которую никто не включал. На кассовых терминалах физической может
 * не быть совсем.
 *
 * Копировать разметку клавиатуры в двадцать окон нельзя — они разъедутся
 * в первый же месяц. Поэтому один крючок: подключается к полю тремя
 * строками и сам решает, буквы показывать или цифры.
 *
 *   const kb = useKeyboard(value, setValue, 'text');
 *   <input {...kb.props} />
 *   {kb.node}
 *
 * Включается настройкой устройства: у кого системная работает, тому
 * вторая только мешает.
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { OnScreenKeyboard } from './OnScreenKeyboard';
import { isDesktopApp } from '../print';

export type KeyboardKind = 'text' | 'number';

/**
 * Включена ли экранная клавиатура на этом устройстве.
 *
 * В программе для Windows — из коробки: касса стоит на планшете,
 * где системной клавиатуры может не быть вовсе, и первый же экран
 * (адрес сервера) без неё не пройти. В браузере — по настройке,
 * как раньше. Выключается явно, галочкой в настройках устройства.
 */
export function oskEnabled(): boolean {
  const v = localStorage.getItem('dastarhan.osk');
  if (v === '1') return true;
  if (v === '0') return false;
  return isDesktopApp();
}

export function useKeyboard(
  value: string,
  onChange: (next: string) => void,
  kind: KeyboardKind = 'text',
) {
  const on = oskEnabled();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const type = useCallback((ch: string) => {
    onChange(value + ch);
    ref.current?.focus();
  }, [value, onChange]);

  const back = useCallback(() => {
    onChange(value.slice(0, -1));
    ref.current?.focus();
  }, [value, onChange]);

  const clear = useCallback(() => {
    onChange('');
    ref.current?.focus();
  }, [onChange]);

  const node: ReactNode = on && open
    ? (
      <OnScreenKeyboard
        digitsOnly={kind === 'number'}
        onKey={type}
        onBackspace={back}
        onClear={clear}
        onClose={() => setOpen(false)}
      />
    )
    : null;

  return {
    /*
     * Разворачивается в поле. Открываем панель по трём событиям, а
     * не по одному.
     *
     * Раньше стоял только onFocus — и через AnyDesk всё работало
     * (мышь фокус даёт), а пальцем на планшете поле молчало: касание
     * не всегда доходит до фокуса, особенно в оболочке Windows.
     * Кассир жал на «0» в поле наличных при закрытии смены и не мог
     * ввести сумму (Дым, 16.08). Теперь панель открывает и касание,
     * и щелчок, и фокус — что сработает первым, то и откроет; заодно
     * ставим фокус сами, чтобы ввод шёл в нужное поле.
     */
    props: {
      ref,
      onFocus: () => { if (on) setOpen(true); },
      onPointerDown: () => {
        if (!on) return;
        setOpen(true);
        // фокус мог не встать сам — ставим явно, иначе цифры уйдут в никуда
        requestAnimationFrame(() => ref.current?.focus());
      },
      onClick: () => { if (on) setOpen(true); },
    },
    node,
    /** открыта ли — окно может добавить отступ снизу, чтобы не перекрыло */
    open: on && open,
  };
}
